import "./discount.styles";

import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import { getColor } from "../../utils/utils.common";
import { selectOnlineOrdersConfigs } from "../../store/kitchen/kitchen.selector";

import {
  ArowSmallIconContainer,
  DiscountContainer,
  DiscountIconContainer,
  DiscountLeft,
  DiscountText,
} from "./discount.styles";
import { ArowSmallIcon, DiscountIcon } from "../../components/icons/index";
import React, { useEffect, useState } from "react";
import StandardModal from "../../components/shared/modal/standardModal.component";
import FormInput from "../../components/form-input/form-input.component";
import { discountFromCallablePayload } from "../../utils/firestore/getDiscounts";
import { loadDiscountByCodeForKitchen } from "../../utils/onlineOrdering/surchargeConfigs";
import { setDiscount } from "../../store/cart/cart.action";
import { selectCartKitchenId, selectCartNotDiscountTotal } from "../../store/cart/cart.selector";

interface DiscountViewProps {
  discountName?: string;
}

const DiscountView = ({ discountName }: DiscountViewProps) => {
  const dispatch = useDispatch();

  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);

  const cartNotDiscountTotal = useSelector(selectCartNotDiscountTotal);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const backgroundSecondary = getColor(
    "background-secondary",
    onlineOrdersConfig
  );
  const backgroundTertiary = getColor(
    "background-tertiary",
    onlineOrdersConfig
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
    setFormFields({
      customerCode: "",
      errors: {},
    });
  };

  const [formFields, setFormFields] = useState({
    customerCode: "",
    errors: {} as Record<string, string>,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormFields((prev) => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: "",
      },
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formFields.customerCode.trim()) {
      newErrors.customerCode = "Code is required";
    }

    setFormFields((prev) => ({
      ...prev,
      errors: newErrors,
    }));

    return Object.keys(newErrors).length === 0;
  };

  const [loading, setLoading] = useState(false);
  const kitchenId = useSelector(selectCartKitchenId);

  const handleDiscountSubmit = async () => {
    if (!validateForm()) return;

    const codeInput = formFields.customerCode.trim().toUpperCase();
    setLoading(true);

    try {
      if (!kitchenId) {
        setFormFields((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            customerCode: "Unable to apply code. Please refresh and try again.",
          },
        }));
        return;
      }

      const { discount: raw } = await loadDiscountByCodeForKitchen(
        kitchenId,
        codeInput,
      );
      const matchedDiscount = discountFromCallablePayload(raw ?? null);

      if (!matchedDiscount) {
        setFormFields((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            customerCode: "Sorry, but this code is not valid.",
          },
        }));
        return;
      }

      if (
        (matchedDiscount.type === "percentage" &&
          matchedDiscount.value >= 100) ||
        (matchedDiscount.type === "amount" &&
          matchedDiscount.value >= cartNotDiscountTotal)
      ) {
        setFormFields((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            customerCode:
              "Discount exceeds order total. Add more items to apply this discount.",
          },
        }));
        return;
      }
      dispatch(setDiscount(matchedDiscount));
      setFormFields({ customerCode: "", errors: {} });
      toggleModal();
    } catch (error) {
      console.error("Failed to fetch discount code:", error);
      setFormFields((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          customerCode:
            "Could not verify this code. Please check your connection and try again.",
        },
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DiscountContainer
        onClick={toggleModal}
        backgroundSecondary={backgroundSecondary}
        backgroundTertiary={backgroundTertiary}
      >
        <DiscountLeft>
          <DiscountIconContainer brandTertiary={brandTertiary}>
            <DiscountIcon size={isMobile ? "small" : "middle"} />
          </DiscountIconContainer>
          <DiscountText brandTertiary={brandTertiary}>
            {discountName
              ? `Code Applied: ${discountName}`
              : "Add a voucher or discount code"}
          </DiscountText>
        </DiscountLeft>
        <ArowSmallIconContainer brandPrimary={brandPrimary}>
          <ArowSmallIcon size={isMobile ? "small" : "middle"} />
        </ArowSmallIconContainer>
      </DiscountContainer>

      <StandardModal
        isOpen={isModalOpen}
        toggleBottomSheet={toggleModal}
        title="Voucher or discount code"
        onConfirm={handleDiscountSubmit}
        confirmLabel="Apply Code"
        loading={loading}
        loadingContent="Checking Code..."
        errorMessage={formFields.errors.customerCode}
      >
        <FormInput
          label="Code"
          type="text"
          required
          name="customerCode"
          value={formFields.customerCode}
          onChange={handleChange}
          placeholder="Enter a code or voucher number"
          autoFocusEnable
        />
      </StandardModal>
    </>
  );
};

export default DiscountView;
